import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  User, Save, X, Image, Plus, Trash2, MapPin, Phone, Mail, 
  Globe, Clock, Users, CheckCircle, Upload, Loader2, Crown, Calendar,
  ArrowLeft, DollarSign, Briefcase, GraduationCap, Award
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';
import { CHILEAN_REGIONS, getCitiesByRegion } from '../../constants/chileanLocations';

export default function ProfessionalEditor({ professional, onSave, onCancel }) {
  // Form state
  var [formData, setFormData] = useState({
    // Basic info
    name: '',
    specialty: '',
    professional_type: '',
    specialization: '',
    license_number: '',
    institution: '',
    bio: '',
    description: '',
    
    // Contact
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    address: '',
    city: '',
    region: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    
    // Pricing
    consultation_price: '',
    session_price: '',
    
    // Plan
    plan_type: 'gratuito',
    plan_level: 0,
    premium_until: '',
    
    // Profile image
    profile_image: '',
    
    // Services
    services: [],
    specialties: [],
    approaches: [],
    languages: [],
    modality: [],
    schedule: '',
    
    // Status
    is_public: true,
    is_verified: false
  });
  
  var [saving, setSaving] = useState(false);
  var [uploading, setUploading] = useState(false);
  var [activeSection, setActiveSection] = useState('info');

  // Professional types - use standard Spanish values
  var professionalTypes = [
    { value: 'Psicólogo/a', label: 'Psicólogo/a' },
    { value: 'Psiquiatra', label: 'Psiquiatra' },
    { value: 'Terapeuta', label: 'Terapeuta' },
    { value: 'Consejero/a', label: 'Consejero/a' },
    { value: 'Trabajador/a Social', label: 'Trabajador/a Social' },
    { value: 'Coach', label: 'Coach' },
    { value: 'Médico', label: 'Médico' },
    { value: 'Enfermero/a', label: 'Enfermero/a' },
    { value: 'Otro', label: 'Otro' }
  ];

  // Services
  var serviceOptions = [
    'Terapia Individual', 'Terapia de Pareja', 'Terapia Familiar', 'Terapia Grupal',
    'Evaluación Psicológica', 'Intervención en Crisis', 'Orientación Vocacional',
    'Tratamiento de Adicciones', 'Tratamiento de Ansiedad', 'Tratamiento de Depresión',
    'Trastornos Alimenticios', 'TDAH', 'Autismo', 'Trauma y TEPT'
  ];

  // Therapeutic approaches
  var approachOptions = [
    'Cognitivo-Conductual', 'Cognitivo', 'Conductual', 'Psicoanalítico', 'Humanista', 'Sistémico',
    'Gestalt', 'EMDR', 'Mindfulness', 'ACT', 'DBT', 'Integrativo', 'Psicoeducativo', 'Narrativo'
  ];

  // Modalities
  var modalityOptions = [
    { value: 'presencial', label: 'Presencial' },
    { value: 'online', label: 'Online' },
    { value: 'domicilio', label: 'A domicilio' }
  ];

  // Plan options
  var planOptions = [
    { value: 'gratuito', label: 'Gratuito', level: 0, color: 'bg-slate-600' },
    { value: 'destacado', label: 'Destacado', level: 1, color: 'bg-blue-600' },
    { value: 'premium', label: 'Premium', level: 2, color: 'bg-amber-600' },
    { value: 'elite', label: 'Elite', level: 3, color: 'bg-purple-600' }
  ];

  var sections = [
    { id: 'info', label: 'Información Básica', icon: User },
    { id: 'contact', label: 'Contacto', icon: Phone },
    { id: 'pricing', label: 'Precios', icon: DollarSign },
    { id: 'plan', label: 'Plan y Suscripción', icon: Crown },
    { id: 'image', label: 'Foto de Perfil', icon: Image },
    { id: 'services', label: 'Servicios y Especialidades', icon: Briefcase },
    { id: 'status', label: 'Estado', icon: CheckCircle }
  ];

  useEffect(function() {
    if (professional) {
      setFormData({
        name: professional.name || '',
        specialty: professional.specialty || professional.specialization || '',
        professional_type: professional.professional_type || '',
        specialization: professional.specialization || '',
        license_number: professional.license_number || '',
        institution: professional.institution || '',
        bio: professional.bio || '',
        description: professional.description || professional.bio || '',
        phone: professional.phone || '',
        whatsapp: professional.whatsapp || '',
        email: professional.email || '',
        website: professional.website || '',
        address: professional.address || '',
        city: professional.city || '',
        region: professional.region || '',
        facebook: professional.facebook || '',
        instagram: professional.instagram || '',
        linkedin: professional.linkedin || '',
        consultation_price: professional.consultation_price || '',
        session_price: professional.session_price || '',
        plan_type: professional.plan_type || 'gratuito',
        plan_level: professional.plan_level || 0,
        premium_until: professional.premium_until ? professional.premium_until.split('T')[0] : '',
        profile_image: professional.profile_image || professional.picture || '',
        services: professional.services || [],
        specialties: professional.specialties || [],
        approaches: professional.approaches || [],
        languages: professional.languages || ['Español'],
        modality: Array.isArray(professional.modality) ? professional.modality : (professional.modality ? [professional.modality] : ['presencial']),
        schedule: professional.schedule || '',
        is_public: professional.is_public !== false,
        is_verified: professional.is_verified || false
      });
    }
  }, [professional]);

  function updateField(field, value) {
    setFormData(function(prev) {
      var updated = Object.assign({}, prev);
      updated[field] = value;
      return updated;
    });
  }

  function toggleArrayItem(field, item) {
    setFormData(function(prev) {
      var arr = prev[field] || [];
      var newArr = arr.includes(item) 
        ? arr.filter(function(i) { return i !== item; })
        : arr.concat([item]);
      var updated = Object.assign({}, prev);
      updated[field] = newArr;
      return updated;
    });
  }

  async function handleImageUpload(e) {
    var file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no debe superar 10MB');
      return;
    }
    
    // Check file type
    var allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    var allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    var ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }

    setUploading(true);
    var formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      var res = await authAxios.post(API + '/upload/image', formDataUpload, {
        withCredentials: true
      });
      updateField('profile_image', res.data.url);
      toast.success('Imagen subida correctamente');
    } catch (err) {
      var errorMsg = err.response?.data?.detail || 'Error al subir imagen';
      toast.error(errorMsg);
    }
    setUploading(false);
  }

  async function handleSave() {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      var dataToSend = Object.assign({}, formData);
      
      // Convert empty strings to null for optional fields
      if (!dataToSend.premium_until) dataToSend.premium_until = null;
      if (!dataToSend.consultation_price) dataToSend.consultation_price = null;
      if (!dataToSend.session_price) dataToSend.session_price = null;

      await authAxios.put(
        API + '/admin/professionals/' + professional.user_id,
        dataToSend,
        { withCredentials: true }
      );
      toast.success('Profesional actualizado exitosamente');
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onCancel} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {professional ? 'Editar Profesional' : 'Nuevo Profesional'}
            </h1>
            <p className="text-slate-400">{formData.name || 'Sin nombre'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="border-slate-700 text-slate-300">
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Cambios
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar Navigation */}
        <div className="col-span-3">
          <Card className="bg-slate-900 border-slate-800 sticky top-6">
            <CardContent className="p-2">
              {sections.map(function(section) {
                var Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={function() { setActiveSection(section.id); }}
                    className={'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ' +
                      (activeSection === section.id 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white')}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{section.label}</span>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-9 space-y-6">
          {/* Información Básica */}
          {activeSection === 'info' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-400" />
                  Información Básica
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nombre Completo *</Label>
                    <Input 
                      value={formData.name}
                      onChange={function(e) { updateField('name', e.target.value); }}
                      placeholder="Dr. Juan Pérez"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Tipo de Profesional</Label>
                    <select
                      value={formData.professional_type}
                      onChange={function(e) { updateField('professional_type', e.target.value); }}
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      <option value="">Seleccionar...</option>
                      {professionalTypes.map(function(type) {
                        return <option key={type.value} value={type.value}>{type.label}</option>;
                      })}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Especialidad Principal</Label>
                    <Input 
                      value={formData.specialty}
                      onChange={function(e) { updateField('specialty', e.target.value); }}
                      placeholder="Psicología Clínica"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Número de Licencia/Registro</Label>
                    <Input 
                      value={formData.license_number}
                      onChange={function(e) { updateField('license_number', e.target.value); }}
                      placeholder="PSI-12345"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Institución / Afiliación</Label>
                  <Input 
                    value={formData.institution}
                    onChange={function(e) { updateField('institution', e.target.value); }}
                    placeholder="Universidad de Chile, Colegio de Psicólogos"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Biografía / Descripción</Label>
                  <Textarea 
                    value={formData.bio}
                    onChange={function(e) { updateField('bio', e.target.value); }}
                    placeholder="Describe tu experiencia, formación y enfoque terapéutico..."
                    className="bg-slate-800 border-slate-700 text-white min-h-[120px]"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contacto */}
          {activeSection === 'contact' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Phone className="w-5 h-5 text-emerald-400" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Teléfono</Label>
                    <Input 
                      value={formData.phone}
                      onChange={function(e) { updateField('phone', e.target.value); }}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">WhatsApp</Label>
                    <Input 
                      value={formData.whatsapp}
                      onChange={function(e) { updateField('whatsapp', e.target.value); }}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={function(e) { updateField('email', e.target.value); }}
                      placeholder="contacto@profesional.cl"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Sitio Web</Label>
                    <Input 
                      value={formData.website}
                      onChange={function(e) { updateField('website', e.target.value); }}
                      placeholder="https://www.mipagina.cl"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Dirección de Consulta</Label>
                  <Input 
                    value={formData.address}
                    onChange={function(e) { updateField('address', e.target.value); }}
                    placeholder="Av. Providencia 1234, Oficina 567"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Región</Label>
                    <Select 
                      value={formData.region || ''} 
                      onValueChange={function(v) { 
                        updateField('region', v); 
                        // Clear city when region changes
                        updateField('city', '');
                      }}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Seleccionar región" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {CHILEAN_REGIONS.map(function(r) {
                          return <SelectItem key={r.value} value={r.value} className="text-slate-200">{r.label}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Ciudad</Label>
                    <Select 
                      value={formData.city || ''} 
                      onValueChange={function(v) { updateField('city', v); }}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Seleccionar ciudad" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {(formData.region ? getCitiesByRegion(formData.region) : []).map(function(c) {
                          return <SelectItem key={c} value={c} className="text-slate-200">{c}</SelectItem>;
                        })}
                        <SelectItem value="otra" className="text-slate-200">Otra ciudad</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <Label className="text-slate-300 mb-3 block">Redes Sociales</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-400 text-xs">Facebook</Label>
                      <Input 
                        value={formData.facebook}
                        onChange={function(e) { updateField('facebook', e.target.value); }}
                        placeholder="facebook.com/..."
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Instagram</Label>
                      <Input 
                        value={formData.instagram}
                        onChange={function(e) { updateField('instagram', e.target.value); }}
                        placeholder="@usuario"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">LinkedIn</Label>
                      <Input 
                        value={formData.linkedin}
                        onChange={function(e) { updateField('linkedin', e.target.value); }}
                        placeholder="linkedin.com/in/..."
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Precios */}
          {activeSection === 'pricing' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Precios de Consulta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Precio Primera Consulta (CLP)</Label>
                    <Input 
                      type="number"
                      value={formData.consultation_price}
                      onChange={function(e) { updateField('consultation_price', e.target.value ? parseInt(e.target.value) : ''); }}
                      placeholder="Ej: 40000"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Precio de la primera sesión o evaluación</p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Precio Sesión Regular (CLP)</Label>
                    <Input 
                      type="number"
                      value={formData.session_price}
                      onChange={function(e) { updateField('session_price', e.target.value ? parseInt(e.target.value) : ''); }}
                      placeholder="Ej: 35000"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Precio de sesiones de seguimiento</p>
                  </div>
                </div>
                
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h4 className="text-white font-medium mb-2">Vista Previa</h4>
                  <p className="text-slate-300">
                    {formData.consultation_price || formData.session_price ? (
                      <span>
                        {formData.consultation_price && (
                          <span className="text-emerald-400 font-semibold">
                            Primera consulta: ${Number(formData.consultation_price).toLocaleString('es-CL')}
                          </span>
                        )}
                        {formData.consultation_price && formData.session_price && ' | '}
                        {formData.session_price && (
                          <span className="text-emerald-400 font-semibold">
                            Sesión: ${Number(formData.session_price).toLocaleString('es-CL')}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-500">No especificado</span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plan y Suscripción */}
          {activeSection === 'plan' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Plan y Suscripción
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-slate-300 mb-3 block">Tipo de Plan</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {planOptions.map(function(plan) {
                      return (
                        <button
                          key={plan.value}
                          type="button"
                          onClick={function() { 
                            updateField('plan_type', plan.value);
                            updateField('plan_level', plan.level);
                          }}
                          className={'p-4 rounded-lg border-2 transition-all ' +
                            (formData.plan_type === plan.value 
                              ? 'border-emerald-500 bg-emerald-500/10' 
                              : 'border-slate-700 hover:border-slate-600')}
                        >
                          <div className={'w-full h-2 rounded mb-2 ' + plan.color}></div>
                          <p className="text-white font-medium">{plan.label}</p>
                          <p className="text-xs text-slate-400">Nivel {plan.level}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nivel de Prioridad</Label>
                    <Input 
                      type="number"
                      min="0"
                      max="10"
                      value={formData.plan_level}
                      onChange={function(e) { updateField('plan_level', parseInt(e.target.value) || 0); }}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Mayor nivel = aparece primero en listados</p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Válido Hasta</Label>
                    <Input 
                      type="date"
                      value={formData.premium_until}
                      onChange={function(e) { updateField('premium_until', e.target.value); }}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Foto de Perfil */}
          {activeSection === 'image' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Image className="w-5 h-5 text-emerald-400" />
                  Foto de Perfil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-6">
                  {/* Current Image */}
                  <div className="w-40 h-40 rounded-full bg-slate-800 border-2 border-slate-700 overflow-hidden flex-shrink-0">
                    {formData.profile_image ? (
                      <img 
                        src={formData.profile_image} 
                        alt="Perfil" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 text-slate-600" />
                      </div>
                    )}
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <Label className="text-slate-300 mb-2 block">Subir Nueva Foto</Label>
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg border border-slate-700 transition-colors">
                            {uploading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            Seleccionar Imagen
                          </span>
                        </label>
                        {formData.profile_image && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={function() { updateField('profile_image', ''); }}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">JPG, PNG o WebP. Máximo 5MB. Recomendado: 400x400px</p>
                    </div>

                    <div>
                      <Label className="text-slate-300">O pegar URL de imagen</Label>
                      <Input 
                        value={formData.profile_image}
                        onChange={function(e) { updateField('profile_image', e.target.value); }}
                        placeholder="https://..."
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Servicios y Especialidades */}
          {activeSection === 'services' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-emerald-400" />
                  Servicios y Especialidades
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Modalidad */}
                <div>
                  <Label className="text-slate-300 mb-3 block">Modalidad de Atención</Label>
                  <div className="flex flex-wrap gap-2">
                    {modalityOptions.map(function(mod) {
                      var isSelected = formData.modality.includes(mod.value);
                      return (
                        <button
                          key={mod.value}
                          type="button"
                          onClick={function() { toggleArrayItem('modality', mod.value); }}
                          className={'px-4 py-2 rounded-lg border transition-all ' +
                            (isSelected 
                              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' 
                              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                        >
                          {mod.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Servicios */}
                <div>
                  <Label className="text-slate-300 mb-3 block">Servicios que Ofrece</Label>
                  <div className="flex flex-wrap gap-2">
                    {serviceOptions.map(function(service) {
                      var isSelected = formData.services.includes(service);
                      return (
                        <button
                          key={service}
                          type="button"
                          onClick={function() { toggleArrayItem('services', service); }}
                          className={'px-3 py-1.5 rounded-full text-sm border transition-all ' +
                            (isSelected 
                              ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                        >
                          {service}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Enfoques Terapéuticos */}
                <div>
                  <Label className="text-slate-300 mb-3 block">Enfoques Terapéuticos</Label>
                  <div className="flex flex-wrap gap-2">
                    {approachOptions.map(function(approach) {
                      var isSelected = formData.approaches.includes(approach);
                      return (
                        <button
                          key={approach}
                          type="button"
                          onClick={function() { toggleArrayItem('approaches', approach); }}
                          className={'px-3 py-1.5 rounded-full text-sm border transition-all ' +
                            (isSelected 
                              ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                        >
                          {approach}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Horario */}
                <div>
                  <Label className="text-slate-300">Horario de Atención</Label>
                  <Input 
                    value={formData.schedule}
                    onChange={function(e) { updateField('schedule', e.target.value); }}
                    placeholder="Lun-Vie 9:00-18:00, Sáb 9:00-13:00"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Estado */}
          {activeSection === 'status' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Estado del Perfil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
                  <div>
                    <p className="text-white font-medium">Perfil Público</p>
                    <p className="text-sm text-slate-400">El perfil aparece en el directorio de profesionales</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={function(e) { updateField('is_public', e.target.checked); }}
                    className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
                  <div>
                    <p className="text-white font-medium">Perfil Verificado</p>
                    <p className="text-sm text-slate-400">Muestra el badge de verificación en el perfil</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_verified}
                    onChange={function(e) { updateField('is_verified', e.target.checked); }}
                    className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                  />
                </label>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
